import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { AmountInputProps } from '../../../../types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import { AccountType, SubmissionState } from '../../../../constants';
import { selectAnyHasLockedState, selectSomeHasError } from '../../../../selectors/entries';
import { selectSubmission } from '../../../../selectors/submission';
import AmountInput from '../AmountInput';
import AccountSelect, { AccountSelectProps } from './AccountSelect';

import styles from './MainAccount.module.scss';

// ---

type StoreProps = {
  disabled: boolean;
};

type MainAccountProps = {
  betId?: number;
  account: AccountType;
  placeholder: string;
  freeBet?: FreeBet | null;
  error?: boolean;
  className?: string;
  inputProps: AmountInputProps;
  disabledTitle: AccountSelectProps['disabledTitle'];
};

const MainAccount: FC<MainAccountProps & StoreProps> = ({
  betId,
  account,
  placeholder,
  disabled,
  error,
  className,
  inputProps,
  disabledTitle,
}) => {
  const [isFocus, setIsFocus] = useState(false);

  const _className = classNames(styles.inputContainer, {
    [styles.disabled]: disabled,
    [styles.error]: error,
    [styles.focus]: isFocus,
  }, className);

  const focusInputHandler = useCallback(() => {
    setIsFocus(true);
  }, []);

  const blurInputHandler = useCallback(() => {
    setIsFocus(false);
  }, []);

  return (
    <div className={_className}
         data-test="mainAccount-container">
      <AmountInput name={inputProps.name}
                   betId={betId}
                   data-test="mainAccount-input"
                   value={inputProps.value}
                   placeholder={placeholder}
                   inputClassName={styles.input}
                   unitsClassName={styles.units}
                   onChange={inputProps.onChange}
                   onFocus={focusInputHandler}
                   onBlur={blurInputHandler}
                   account={account} />
      <AccountSelect disabledTitle={disabledTitle}
                     inputProps={inputProps}
                     account={account}
                     className={styles.select} />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);

  return {
    disabled: selectSubmission(state) !== SubmissionState.AWAITING || hasEntriesErrors,
  };
};

export default connect(mapStateToProps)(MainAccount);
